import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { getUrl } from "../../helpers/getUrl";
import { setShowErrorModal } from "./preferenceSlice";
import { fetchStatus } from "./preferenceSlice";

const initialState = {
  pipelinePreference: undefined,
  dealStagePreference: undefined,
  invoicePreference: undefined,
  attachPDFToInvoice: false,
  isInvoiceSyncRulesSaved: false,
  isInvoiceDefaultMappingsSaved: false,
  selectedPipelines: [],
  pipelines: {
    loading: false,
    pipelines: [],
    error: "",
    currentRequestId: undefined,
  },
  dealStages: {
    loading: false,
    dealStages: [],
    error: "",
    currentRequestId: undefined,
  },
  invoiceDropdownFields: [
    {
      title: "Create draft invoice",
      value: "invoice",
    },
    {
      title: "Create draft order",
      value: "order",
    },
  ],
  orderDepartmentPreference: undefined,
  orderCurrencyPreference: undefined,
};

// Fetch pipelines
export const fetchPipelines = createAsyncThunk(
  "invoice/fetchPipelines",
  async (portalId, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_FETCH_PIPELINES")}?portalId=${portalId}`
      );
      return response;
    } catch (error) {
      let errorMessage;
      if (error?.response && error.response?.data?.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    }
  }
);

export const fetchDealStages = createAsyncThunk(
  "invoice/fetchDealStages",
  async ({ portalId, pipelineId }, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl(
          "REACT_APP_GET_DEAL_STAGES"
        )}?portalId=${portalId}&pipelineId=${pipelineId}`
      );

      return response;
    } catch (error) {
      let errorMessage;
      if (error?.response && error.response?.data?.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    }
  }
);

const invoiceSlice = createSlice({
  name: "invoiceSlice",
  initialState,
  reducers: {
    /**
     * Reducer updates the state for the invoice tab dropdown fields
     */
    setSelectedInvoiceFields(state, { payload }) {
      if (payload.selectingFor === "pipelines") {
        state.pipelinePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.dealStagePreference = undefined;
        state.isInvoiceSyncRulesSaved = false;
      } else if (payload.selectingFor === "dealStages") {
        state.dealStagePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isInvoiceSyncRulesSaved = false;
      } else if (payload.selectingFor === "preferenceForCreation") {
        state.invoicePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isInvoiceSyncRulesSaved = false;
      } else if (payload.selectingFor === "orderDepartment") {
        state.orderDepartmentPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isInvoiceDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "orderCurrency") {
        state.orderCurrencyPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isInvoiceDefaultMappingsSaved = false;
      }
    },
    /**
     * This function is used to delete the selected pipelines
     */
    onDeletePipeline(state, { payload }) {
      state.selectedPipelines.splice(payload.index, 1);
      state.isInvoiceSyncRulesSaved = false;
    },
    /**
     * @description Reducer updates the state of the selected pipelines and deal stages
     */
    onAddPipeline(state) {
      if (state.pipelinePreference && state.dealStagePreference) {
        state.selectedPipelines.push({
          pipeline: state.pipelinePreference,
          dealStage: state.dealStagePreference,
          generate: {
            name: state.invoicePreference.name,
            value: state.invoicePreference.value,
          },
        });
        state.isInvoiceSyncRulesSaved = false;
        state.invoicePreference = undefined;
        state.pipelinePreference = undefined;
        state.dealStagePreference = undefined;
      }
    },
    /**
     * @description Reducer updates that state for checkbox
     */
    onCheckAddPDF(state) {
      state.attachPDFToInvoice = !state.attachPDFToInvoice;
      state.isInvoiceSyncRulesSaved = false;
    },
    setIsInvoiceSyncRulesSaved(state) {
      state.isInvoiceSyncRulesSaved = true;
    },
    setIsInvoiceDefaultMappingsSaved(state) {
      state.isInvoiceDefaultMappingsSaved = true;
    },
    /**
     * @description Reducer updates the states of some fields when economic is disconnected
     */
    clearInvoicePreferences(state) {
      state.pipelinePreference = undefined;
      state.dealStagePreference = undefined;
      state.invoicePreference = undefined;
      state.attachPDFToInvoice = false;
      state.selectedPipelines = [];
      state.isInvoiceSyncRulesSaved = false;
    },
    /**
     * @description Reducer deletes the state of pipelines
     */
    onDeleteSelectedPipeline(state) {
      state.pipelinePreference = undefined;
      state.dealStagePreference = undefined;
      state.invoicePreference = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPipelines.pending, (state, action) => {
      if (!state.pipelines.loading) {
        state.pipelines.loading = true;
        state.pipelines.currentRequestId = action.meta.requestId;
      }
    });

    builder.addCase(fetchPipelines.fulfilled, (state, action) => {
      if (
        state.pipelines.loading &&
        state.pipelines.currentRequestId === action.meta.requestId
      ) {
        state.pipelines.loading = false;
        state.pipelines.pipelines = action.payload;
        state.pipelines.error = "";
        state.pipelines.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchPipelines.rejected, (state, action) => {
      if (
        state.pipelines.loading &&
        state.pipelines.currentRequestId === action.meta.requestId
      ) {
        state.pipelines.loading = false;
        state.pipelines.pipelines = [];
        state.pipelines.error = action.error.message ?? "";
        state.pipelines.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchDealStages.pending, (state, action) => {
      if (!state.dealStages.loading) {
        state.dealStages.loading = true;
        state.dealStages.currentRequestId = action.meta.requestId;
      }
    });

    builder.addCase(fetchDealStages.fulfilled, (state, action) => {
      if (
        state.dealStages.loading &&
        state.dealStages.currentRequestId === action.meta.requestId
      ) {
        state.dealStages.loading = false;
        state.dealStages.dealStages = action.payload;
        state.dealStages.error = "";
        state.dealStages.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchDealStages.rejected, (state, action) => {
      if (
        state.dealStages.loading &&
        state.dealStages.currentRequestId === action.meta.requestId
      ) {
        state.dealStages.loading = false;
        state.dealStages.dealStages = [];
        state.dealStages.error = action.error.message ?? "";
        state.dealStages.currentRequestId = undefined;
      }
    });

    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      const { preference } = action.payload;

      if (preference !== undefined && Object.keys(preference).length > 0) {
        const { order } = preference;

        if (order?.triggerSettings) {
          state.selectedPipelines = order?.triggerSettings;
        }

        if (order?.associateInvoicePdf) {
          state.attachPDFToInvoice =
            order?.associateInvoicePdf?.association === undefined
              ? false
              : order.associateInvoicePdf.association;
        }

        // if (order?.txFields) {
        //   const { department, currency } = order.txFields;

        //   state.orderCurrencyPreference = currency?.value
        //     ? currency
        //     : undefined;
        //   state.orderDepartmentPreference = department?.value
        //     ? department
        //     : undefined;
        // }
      }

      // if (state.orderCurrencyPreference && state.orderDepartmentPreference) {
      //   state.isInvoiceDefaultMappingsSaved = true;
      // }

      if (state.selectedPipelines.length > 0) {
        state.isInvoiceSyncRulesSaved = true;
      }
    });
  },
});

export default invoiceSlice.reducer;
export const {
  onChangePreferenceForProducts,
  setSelectedInvoiceFields,
  onDeletePipeline,
  onAddPipeline,
  onCheckAddPDF,
  setIsInvoiceSyncRulesSaved,
  clearInvoicePreferences,
  onChangePreferenceForSearchParameters,
  onDeleteSelectedPipeline,
  setIsInvoiceDefaultMappingsSaved,
} = invoiceSlice.actions;
